import React, { useState, useEffect, useRef } from "react";
import {
  Search,
  Send,
  MessageSquare,
  ArrowLeft,
  Settings,
  MoreVertical,
  X,
  Paperclip,
  Image as ImageIcon,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { profileInfo, getUserList, fetchBlockedUsers, blockUser, unblockUser } from "../../../features/userSlice";
import { fetchMessages, fetchContacts } from "../../../features/chatSlice";
import avater from "../../../assets/img/avater.png"; // Assuming this path is correct
import API from "../../../utils/Api";

/* ================= SOCKET ================= */
// Use environment variable for production, hardcoding for example
const socket = io("http://localhost:5000");

/* ================= UTILITY FUNCTIONS ================= */
const formatTime = (isoTime) => {
  if (!isoTime) return "";
  return new Date(isoTime).toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });
};

/* ================= CHAT THREAD COMPONENT ================= */
const ChatThread = ({ thread, onSelect, isActive, defaultAvatar, resolveAvatar }) => (
  <div
    onClick={() => onSelect(thread._id)}
    className={`flex items-center p-3 sm:px-4 sm:py-3 cursor-pointer transition-colors duration-200 ease-in-out border-b border-gray-100 last:border-b-0
      ${
        isActive
          ? "bg-indigo-50 border-l-4 border-indigo-600 shadow-sm"
          : "hover:bg-gray-50"
      }`}
  >
    <img
      src={resolveAvatar(thread.avatarUrl || thread.avatar) || defaultAvatar}
      className="w-12 h-12 rounded-full object-cover flex-shrink-0 border border-gray-200"
      alt="avatar"
    />
    <div className="ml-3 truncate flex-1 min-w-0">
      <h3 className="font-semibold text-gray-800 truncate">
        {thread.name || thread.username}
      </h3>
      {/* Example: Display last message or status */}
      <p className="text-sm text-gray-500 truncate mt-0.5">
        {thread.lastMessage?.text || thread.bio || "Click to open chat"}
      </p>
    </div>
    {thread.unreadCount > 0 && (
      <span className="ml-2 text-xs bg-indigo-600 text-white rounded-full px-2 py-0.5">
        {thread.unreadCount}
      </span>
    )}
    {/* Optional: Add unread badge or last message time here */}
  </div>
);

/* ================= CHAT BUBBLE COMPONENT ================= */
const ChatBubble = ({ message, time, isMine, avatar, currentUserId, onViewOnce }) => {
  const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
  const resolvedFileUrl = message.fileUrl
    ? message.fileUrl.startsWith("http")
      ? message.fileUrl
      : `${apiBase}${message.fileUrl}`
    : "";

  const viewOnceSeen = message.isViewOnce && message.viewOnceSeenBy?.some(
    (id) => id?.toString() === currentUserId
  );

  const renderContent = () => {
    if (message.messageType === "image" && resolvedFileUrl) {
      if (message.isViewOnce && !isMine) {
        if (viewOnceSeen) {
          return (
            <div className="text-xs text-gray-500">View-once image</div>
          );
        }
        return (
          <button
            onClick={() => onViewOnce(message)}
            className="text-sm text-indigo-600 hover:text-indigo-700"
          >
            View once image
          </button>
        );
      }

      return (
        <img
          src={resolvedFileUrl}
          alt={message.fileName || "Image"}
          className="max-w-xs md:max-w-sm lg:max-w-md rounded-lg border"
        />
      );
    }

    if (message.messageType === "file" && resolvedFileUrl) {
      if (message.isViewOnce && !isMine) {
        if (viewOnceSeen) {
          return (
            <div className="text-xs text-gray-500">View-once file</div>
          );
        }
        return (
          <button
            onClick={() => onViewOnce(message)}
            className="text-sm text-indigo-600 hover:text-indigo-700"
          >
            View once file
          </button>
        );
      }

      return (
        <a
          href={resolvedFileUrl}
          target="_blank"
          rel="noreferrer"
          className="text-sm text-indigo-600 hover:text-indigo-700 underline"
        >
          {message.fileName || "Download file"}
        </a>
      );
    }

    return <span>{message.text}</span>;
  };

  const statusLabel = () => {
    if (!isMine) return "";
    if (message.status === "sending") return "Sending";
    if (message.isRead) return "Seen";
    return "Delivered";
  };

  return (
    <div className={`flex ${isMine ? "justify-end" : "justify-start"} mb-4`}>
      {!isMine && (
        <img
          src={avatar}
          className="w-8 h-8 rounded-full mr-2 self-start flex-shrink-0 object-cover border border-gray-100"
          alt="Partner avatar"
        />
      )}
      <div className="max-w-xs md:max-w-md lg:max-w-lg">
        <div
          className={`px-4 py-2 text-sm shadow-md transition-all duration-300
            ${
              isMine
                ? "bg-indigo-600 text-white rounded-t-xl rounded-bl-xl rounded-br-md"
                : "bg-white text-gray-800 rounded-t-xl rounded-br-xl rounded-bl-md border border-gray-200"
            }`}
        >
          {renderContent()}
        </div>
        <p
          className={`text-xs mt-1 transition-opacity duration-300 ${
            isMine ? "text-gray-400 text-right mr-1" : "text-gray-500 ml-1"
          }`}
        >
          {time} {statusLabel() && <span>• {statusLabel()}</span>}
        </p>
      </div>
    </div>
  );
};

/* ================= SIDEBAR COMPONENT ================= */
const ChatSidebar = ({
  user,
  userList,
  contacts,
  activeThreadId,
  selectThread,
  avater,
  isMobile,
  activePartner, // Pass activePartner to determine visibility on mobile
  searchTerm,
  setSearchTerm,
  resolveAvatar,
  onOpenSettings,
}) => {
  const isHidden = isMobile && activeThreadId;
  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredUsers = userList.filter((u) => {
    const name = (u.name || "").toLowerCase();
    const username = (u.username || "").toLowerCase();
    return name.includes(normalizedSearch) || username.includes(normalizedSearch);
  });
  const listToShow = normalizedSearch ? filteredUsers : contacts;

  return (
    <div
      className={`w-full md:w-80 lg:w-96 bg-white border-r border-gray-200 flex flex-col transition-all duration-300
        ${isHidden ? "hidden" : "flex"}`}
    >
      {/* Sidebar Header */}
      <div className="p-4 bg-white border-b border-gray-200 shadow-sm">
        {/* User Profile/Settings/Back Button */}
        <div className="flex justify-between items-center mb-4">
          <Link
            to="/"
            className="flex items-center space-x-1 p-2 rounded-full text-gray-700 hover:bg-gray-100 transition"
          >
            <ArrowLeft size={20} />
          </Link>

          <h2 className="text-xl font-bold text-gray-900 truncate flex-1 text-center">
            Messages
          </h2>
          <button
            className="p-2 rounded-full text-gray-700 hover:bg-gray-100 transition"
            onClick={onOpenSettings}
          >
            <Settings size={20} />
          </button>
        </div>

        {/* Search Bar - Modernized */}
        <div className="relative">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-10 py-2 text-sm rounded-lg border border-gray-300 shadow-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-gray-50"
          />
          {searchTerm && (
            <button
              type="button"
              onClick={() => setSearchTerm("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {normalizedSearch ? `${filteredUsers.length} result(s)` : `${contacts.length} chats`}
        </p>
      </div>

      {/* Chat Thread List */}
      <div className="flex-1 overflow-y-auto">
        {listToShow.length > 0 ? (
          listToShow.map((u) => (
            <ChatThread
              key={u._id}
              thread={u}
              isActive={u._id === activeThreadId}
              onSelect={selectThread}
              defaultAvatar={avater}
              resolveAvatar={resolveAvatar}
            />
          ))
        ) : (
          <div className="p-4 text-center text-gray-500">
            {normalizedSearch ? "No users found." : "Search to start a new chat."}
          </div>
        )}
      </div>
    </div>
  );
};

/* ================= CHAT WINDOW COMPONENT ================= */
const ChatWindow = ({
  activePartner,
  userId,
  messages,
  chatRef,
  currentMessage,
  setCurrentMessage,
  sendMessage,
  sendFileMessage,
  onViewOnce,
  attachedFile,
  setAttachedFile,
  filePreview,
  setFilePreview,
  viewOnce,
  setViewOnce,
  onBlockToggle,
  isBlocked,
  resolveAvatar,
  showMenu,
  setShowMenu,
  onDeleteChat,
  avater,
  isMobile,
  navigate,
  setActiveThreadId,
}) => {
  const isHidden = isMobile && !activePartner;

  return (
    <div
      className={`flex-1 flex-col bg-gray-50 transition-all duration-300 ${
        isHidden ? "hidden" : "flex"
      }`}
    >
      {activePartner ? (
        <>
          {/* Header */}
          <div className="flex items-center justify-between p-4 bg-white border-b border-gray-200 shadow-md">
            <div className="flex items-center">
              <button
                className="md:hidden mr-3 p-2 rounded-full hover:bg-gray-100 transition"
                onClick={() => {
                  setActiveThreadId("");
                  navigate("/chat");
                }}
              >
                <ArrowLeft size={20} />
              </button>

              <img
                src={resolveAvatar(activePartner.avatarUrl || activePartner.avatar) || avater}
                className="w-10 h-10 rounded-full object-cover border border-gray-200"
                alt="Partner avatar"
              />
              <div className="ml-3">
                <h3 className="font-bold text-gray-900 leading-tight">
                  {activePartner.name || activePartner.username}
                </h3>
                <p className="text-xs text-gray-500">
                  {activePartner.bio || "Online"}
                </p>
              </div>
            </div>
            <div className="relative">
              <button
                className="p-2 rounded-full text-gray-600 hover:bg-gray-100 transition"
                onClick={() => setShowMenu((prev) => !prev)}
              >
                <MoreVertical size={20} />
              </button>
              {showMenu && (
                <div className="absolute right-0 mt-2 w-40 bg-white border rounded-lg shadow-lg z-20">
                  <button
                    className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50"
                    onClick={() => {
                      onBlockToggle(activePartner);
                      setShowMenu(false);
                    }}
                  >
                    {isBlocked ? "Unblock user" : "Block user"}
                  </button>
                  <button
                    className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    onClick={() => {
                      onDeleteChat(activePartner);
                      setShowMenu(false);
                    }}
                  >
                    Delete chat
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Messages Area */}
          <div
            ref={chatRef}
            className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-100" // Use a subtle background for the chat area
          >
            {messages.map((m) => (
              <ChatBubble
                key={m._id}
                message={m}
                isMine={m.sender === userId}
                avatar={resolveAvatar(activePartner.avatarUrl || activePartner.avatar) || avater}
                time={formatTime(m.createdAt)}
                currentUserId={userId}
                onViewOnce={onViewOnce}
              />
            ))}
          </div>

          {/* Input Area */}
          <div className="p-4 bg-white border-t border-gray-200 flex items-center shadow-inner">
            <div className="flex items-center gap-2 mr-2">
              <label className="p-2 rounded-full text-indigo-600 hover:bg-indigo-50 transition cursor-pointer">
                <ImageIcon size={20} />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (filePreview) {
                      URL.revokeObjectURL(filePreview);
                    }
                    setAttachedFile(file);
                    setFilePreview(URL.createObjectURL(file));
                  }}
                />
              </label>
              <label className="p-2 rounded-full text-indigo-600 hover:bg-indigo-50 transition cursor-pointer">
                <Paperclip size={20} />
                <input
                  type="file"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (filePreview) {
                      URL.revokeObjectURL(filePreview);
                    }
                    setAttachedFile(file);
                    setFilePreview("");
                  }}
                />
              </label>
            </div>

            <div className="flex-1">
              <input
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  if (attachedFile) {
                    sendFileMessage();
                  } else {
                    sendMessage();
                  }
                }}
                className="w-full bg-gray-100 border border-gray-300 rounded-full px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition duration-150"
                placeholder="Type a message..."
              />
              {isBlocked && (
                <p className="mt-2 text-xs text-red-500">You blocked this user. Unblock to send messages.</p>
              )}
              {attachedFile && (
                <div className="mt-2 bg-gray-50 border border-gray-200 rounded-lg p-2 flex items-center justify-between text-xs">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-700">
                      {attachedFile.name}
                    </span>
                    {filePreview && (
                      <img
                        src={filePreview}
                        alt="Preview"
                        className="w-10 h-10 object-cover rounded border"
                      />
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (filePreview) {
                        URL.revokeObjectURL(filePreview);
                      }
                      setAttachedFile(null);
                      setFilePreview("");
                      setViewOnce(false);
                    }}
                    className="text-red-500 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>

            <label className="ml-2 text-xs text-gray-600 flex items-center gap-1">
              <input
                type="checkbox"
                checked={viewOnce}
                onChange={(e) => setViewOnce(e.target.checked)}
                disabled={!attachedFile}
              />
              View once
            </label>

            <button
              onClick={() => {
                if (attachedFile) {
                  sendFileMessage();
                } else {
                  sendMessage();
                }
              }}
              className="ml-3 p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition duration-150 shadow-lg disabled:bg-indigo-300"
              disabled={(!currentMessage.trim() && !attachedFile) || isBlocked}
            >
              <Send size={20} />
            </button>
          </div>
        </>
      ) : (
        /* Welcome Screen (No chat selected) */
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <div className="w-24 h-24 flex items-center justify-center rounded-full bg-indigo-100 mb-6">
            <MessageSquare size={48} className="text-indigo-600" />
          </div>

          <h3 className="text-2xl font-bold text-gray-800">
            Welcome to the Chat
          </h3>

          <p className="text-md text-gray-500 mt-2 max-w-sm">
            Select a conversation from the left sidebar to start messaging and connect with others.
          </p>
        </div>
      )}
    </div>
  );
};

/* ================= MAIN PAGE COMPONENT ================= */
const MessagesPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { partnerId } = useParams();

  const { user, userList = [], blockedUsers = [] } = useSelector((s) => s.user);
  const { messages: fetchedMessages = [], contacts = [] } = useSelector((s) => s.chat);

  const userId = user?._id;
  const chatRef = useRef(null);
  const isMobile = window.innerWidth < 768;

  const [activeThreadId, setActiveThreadId] = useState("");
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);
  const [filePreview, setFilePreview] = useState("");
  const [viewOnce, setViewOnce] = useState(false);
  const [showMenu, setShowMenu] = useState(false);
  const [showBlockedModal, setShowBlockedModal] = useState(false);

  const activePartner = userList.find((u) => u._id === activeThreadId);

  /* ===== INITIAL LOAD, URL → STATE, FETCH MESSAGES, SET MESSAGES, SOCKET, AUTO SCROLL ===== */
  useEffect(() => {
    dispatch(profileInfo());
    dispatch(getUserList());
    dispatch(fetchContacts());
    dispatch(fetchBlockedUsers());
  }, [dispatch]);

  useEffect(() => {
    if (partnerId) {
      setActiveThreadId(partnerId);
    }
  }, [partnerId]);

  useEffect(() => {
    if (activeThreadId) {
      dispatch(fetchMessages(activeThreadId));
      API.put(`/chats/read-all/${activeThreadId}`).then(() => dispatch(fetchContacts()));
    }
  }, [activeThreadId, dispatch]);

  useEffect(() => {
    const sorted = [...fetchedMessages].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );
    setMessages(sorted);
  }, [fetchedMessages]);

  useEffect(() => {
    if (!userId) return;
    socket.emit("joinChat", userId);
    const handleReceiveMessage = (msg) => {
      setMessages((prev) => {
        if (prev.some((m) => m._id === msg._id)) return prev;
        return [...prev, msg];
      });
      dispatch(fetchContacts());
    };
    socket.on("receiveMessage", handleReceiveMessage);
    return () => {
      socket.off("receiveMessage", handleReceiveMessage);
    };
  }, [userId]);

  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  /* ===== HANDLERS ===== */
  const selectThread = (id) => {
    setActiveThreadId(id);
    navigate(`/chat/${id}`);
    setSearchTerm("");
  };

  const sendMessage = () => {
    if (!currentMessage.trim() || !activeThreadId) return;

    // Optimistic UI update
    const tempMessage = {
      _id: Date.now().toString(),
      sender: userId,
      receiver: activeThreadId,
      text: currentMessage,
      createdAt: new Date().toISOString(),
      status: "sending", // Add a temporary status
    };

    setMessages((prev) => [...prev, tempMessage]);

    socket.emit("sendMessage", {
      senderId: userId,
      receiverId: activeThreadId,
      text: currentMessage,
    });

    setCurrentMessage("");
  };

  const sendFileMessage = async () => {
    if (!attachedFile || !activeThreadId) return;

    const formData = new FormData();
    formData.append("senderId", userId);
    formData.append("receiverId", activeThreadId);
    if (currentMessage.trim()) {
      formData.append("text", currentMessage.trim());
    }
    formData.append("file", attachedFile);
    if (viewOnce) {
      formData.append("isViewOnce", "true");
    }

    try {
      const res = await API.post("/chats/send", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.data?.message) {
        setMessages((prev) => [...prev, res.data.message]);
        dispatch(fetchContacts());
      }
    } catch (error) {
      console.error("Failed to send file message", error);
    } finally {
      if (filePreview) {
        URL.revokeObjectURL(filePreview);
      }
      setAttachedFile(null);
      setFilePreview("");
      setViewOnce(false);
      setCurrentMessage("");
    }
  };

  const handleViewOnce = async (message) => {
    if (!message?._id) return;
    const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
    const resolvedUrl = message.fileUrl
      ? message.fileUrl.startsWith("http")
        ? message.fileUrl
        : `${apiBase}${message.fileUrl}`
      : "";

    if (resolvedUrl) {
      window.open(resolvedUrl, "_blank");
    }

    try {
      const res = await API.put(`/chats/view-once/${message._id}`);
      if (res.data?.message) {
        setMessages((prev) =>
          prev.map((m) => (m._id === message._id ? res.data.message : m))
        );
      }
    } catch (error) {
      console.error("Failed to mark view-once", error);
    }
  };

  const apiBase = API.defaults.baseURL?.replace(/\/api\/?$/, "") || "";
  const resolveAvatar = (url) =>
    url ? (url.startsWith("http") ? url : `${apiBase}${url}`) : "";

  const filteredUsers = userList.filter((u) => u._id !== userId);
  const blockedIds = (blockedUsers || []).map((u) => u._id?.toString?.() || u.toString?.());
  const visibleUsers = filteredUsers.filter((u) => !blockedIds.includes(u._id));

  const isBlocked = blockedIds.includes(activePartner?._id);

  const handleBlockToggle = async (target) => {
    if (!target?._id) return;
    if (blockedIds.includes(target._id)) {
      await dispatch(unblockUser(target._id));
    } else {
      await dispatch(blockUser(target._id));
    }
    dispatch(fetchContacts());
  };

  const handleDeleteChat = async (target) => {
    if (!target?._id) return;
    await API.delete(`/chats/${target._id}`);
    setMessages([]);
    setActiveThreadId("");
    navigate("/chat");
    dispatch(fetchContacts());
  };

  if (!userId) return null; // Or show a loading state

  /* ================= JSX ================= */
  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {/* ===== Left Sidebar (Thread List) ===== */}
      <ChatSidebar
        user={user}
        userList={visibleUsers}
        contacts={contacts}
        activeThreadId={activeThreadId}
        selectThread={selectThread}
        avater={avater}
        isMobile={isMobile}
        activePartner={activePartner}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        resolveAvatar={resolveAvatar}
        onOpenSettings={() => setShowBlockedModal(true)}
      />

      {/* ===== Right (Chat Window) ===== */}
      <ChatWindow
        activePartner={activePartner}
        userId={userId}
        messages={messages}
        chatRef={chatRef}
        currentMessage={currentMessage}
        setCurrentMessage={setCurrentMessage}
        sendMessage={sendMessage}
        sendFileMessage={sendFileMessage}
        onViewOnce={handleViewOnce}
        attachedFile={attachedFile}
        setAttachedFile={setAttachedFile}
        filePreview={filePreview}
        setFilePreview={setFilePreview}
        viewOnce={viewOnce}
        setViewOnce={setViewOnce}
        onBlockToggle={handleBlockToggle}
        isBlocked={isBlocked}
        resolveAvatar={resolveAvatar}
        showMenu={showMenu}
        setShowMenu={setShowMenu}
        onDeleteChat={handleDeleteChat}
        avater={avater}
        isMobile={isMobile}
        navigate={navigate}
        setActiveThreadId={setActiveThreadId}
      />

      {showBlockedModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-40">
          <div className="bg-white w-full max-w-sm rounded-lg shadow-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold">Blocked Users</h3>
              <button onClick={() => setShowBlockedModal(false)}>
                <X size={18} />
              </button>
            </div>
            {blockedUsers.length > 0 ? (
              <div className="space-y-2">
                {blockedUsers.map((u) => (
                  <div key={u._id || u} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">
                      {u.name || u.username || "User"}
                    </span>
                    <button
                      onClick={() => handleBlockToggle(u)}
                      className="text-xs text-indigo-600 hover:text-indigo-700"
                    >
                      Unblock
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No blocked users.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MessagesPage;